import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";

const cfg = window.authConfig;

const msal = new PublicClientApplication({
    auth: {
        clientId: cfg.clientId,
        authority: `https://${cfg.tenantSubdomain}.ciamlogin.com/`,
        // Both hosts are required: External ID's discovery document reports
        // a GUID-host issuer ({tenantId}.ciamlogin.com) for a name-based
        // authority, and MSAL v5's issuer validation only accepts that when
        // the issuer host itself is listed here. Without the second entry
        // every sign-in fails with endpoints_resolution_error.
        knownAuthorities: [
            `${cfg.tenantSubdomain}.ciamlogin.com`,
            `${cfg.tenantId}.ciamlogin.com`,
        ],
        redirectUri: window.location.origin + "/",
    },
    cache: { cacheLocation: "sessionStorage" },
});

let initError = null;
let signedInThisLoad = false;

// MSAL renews tokens in a hidden iframe pointed at redirectUri — which, above,
// is this app's own root. So the response lands on a document that would
// otherwise boot the entire SPA inside that iframe, starting a SECOND MSAL
// instance. That second instance throws block_iframe_reload (MSAL refuses to
// initialize on an auth response inside a frame) and consumes the hash before
// the outer instance's monitor can read it, so a renewal that should have
// surfaced as a clean InteractionRequiredAuthError — which getToken() recovers
// from by redirecting to sign-in — instead dies as timed_out and strands the
// user on an error page with no way back.
//
// Nothing may run in that frame. The outer instance reads the hash out of it
// directly (same origin); it needs no code of ours in there.
//
// Narrow on purpose: only a framed document that is actually carrying an auth
// response is skipped, so embedding the site elsewhere still works.
export function isAuthResponseFrame() {
    return window.self !== window.top && /[#?&](code|error|state)=/.test(window.location.href);
}

async function init() {
    if (isAuthResponseFrame()) return;
    try {
        await msal.initialize();
    } catch (err) {
        initError = err;
        console.error("auth: initialization failed", err);
        return;
    }
    let result = null;
    try {
        result = await msal.handleRedirectPromise();
    } catch (err) {
        // A failed sign-in (MFA abandoned, unassigned user, IdP error)
        // must not brick the page: the user has to be able to press
        // Sign in again. Callers surface this via getError().
        initError = err;
        console.error("auth: sign-in redirect failed", err);
    }
    // A non-null result means THIS page load processed a sign-in redirect.
    // An ordinary load by an already-signed-in user resolves to null, which
    // is what separates "just signed in" from "still signed in".
    signedInThisLoad = result?.account != null;
    const account = result?.account ?? msal.getAllAccounts()[0] ?? null;
    if (account) msal.setActiveAccount(account);
}

// `ready` always fulfills — a failed redirect records an error instead of
// leaving a permanently rejected promise that would brick every consumer.
export const ready = init();

export function getError() {
    return initError;
}

export function getAccount() {
    return msal.getActiveAccount();
}

// True only on the page load that completed a sign-in, so callers can
// send someone to the members area once without trapping them there:
// every later load returns false, leaving the public pages reachable.
export function justSignedIn() {
    return signedInThisLoad;
}

export async function signIn() {
    await ready;
    await msal.loginRedirect({ scopes: [cfg.apiScope] });
}

export async function signOut() {
    await ready;
    const account = msal.getActiveAccount();
    await msal.logoutRedirect({
        // Name the session to end and where to land afterward — without
        // these, Microsoft shows an account picker and then strands the
        // user on its own sign-in page instead of returning to the site.
        account,
        logoutHint: account?.idTokenClaims?.login_hint,
        postLogoutRedirectUri: window.location.origin + "/",
    });
}

// The silent renewal ran but could not complete at all: the iframe was
// refused, or never came back with anything readable. These say nothing about
// the credentials — only that the quiet path is unavailable — and the sole way
// out of them is the same as for an expired session, so they are treated
// alike. They are specific to the renewal iframe; a genuine network failure
// surfaces as a different error and still propagates.
const SILENT_RENEWAL_STUCK = ["block_iframe_reload", "monitor_window_timeout", "timed_out"];

// Returns an access token for the API, refreshing silently when possible.
// Interaction-required — and a silent renewal that could not run at all —
// trigger the redirect fallback (the page navigates away; the returned promise
// never resolves). Anything else (network failure, offline) propagates to the
// caller: redirecting an offline user to an unreachable IdP would strand them
// off the page.
export async function getToken() {
    await ready;
    const account = msal.getActiveAccount();
    if (!account) throw new Error("not-signed-in");
    try {
        const result = await msal.acquireTokenSilent({
            scopes: [cfg.apiScope],
            account,
        });
        return result.accessToken;
    } catch (err) {
        const recoverable =
            err instanceof InteractionRequiredAuthError ||
            SILENT_RENEWAL_STUCK.includes(err?.errorCode);
        if (!recoverable) throw err;
        await msal.acquireTokenRedirect({ scopes: [cfg.apiScope], account });
        return new Promise(() => {});
    }
}
